import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Box, FormControl, Select, MenuItem, Typography, IconButton, TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const PHOTO_SELECT_VIRTUAL_THRESHOLD = 300; // above this use index input instead of Select to avoid 11k-item render/selection slowness

export default function PhotoViewerTools({ photoLayers = [], selectedLayerId, onLayerChange, images, currentIndex, setCurrentIndex, annotationCountsByPhotoId }) {
  const currentImage = images[currentIndex] ?? null;
  const [indexInput, setIndexInput] = useState(String(currentIndex + 1));
  const indexInputRef = useRef(null);

  const getAnnotationCount = useCallback((photoId) => {
    if (!photoId || !annotationCountsByPhotoId) return 0;
    const key = String(photoId);
    if (typeof annotationCountsByPhotoId.get === 'function') {
      return annotationCountsByPhotoId.get(key) || 0;
    }
    return annotationCountsByPhotoId[key] || 0;
  }, [annotationCountsByPhotoId]);

  const useIndexInput = images.length > PHOTO_SELECT_VIRTUAL_THRESHOLD;
  const displayIndex = currentIndex + 1;
  useEffect(() => {
    setIndexInput(String(displayIndex));
  }, [displayIndex]);

  const handleIndexSubmit = useCallback((e) => {
    e?.preventDefault?.();
    const n = parseInt(indexInput, 10);
    if (!Number.isNaN(n) && n >= 1 && n <= images.length) {
      setCurrentIndex(n - 1);
    } else {
      setIndexInput(String(displayIndex));
    }
    indexInputRef.current?.blur();
  }, [indexInput, images.length, displayIndex, setCurrentIndex]);

  const photoSelectOptions = useMemo(() => {
    if (useIndexInput) return null;
    return images.map((img, idx) => {
      const count = getAnnotationCount(img.id);
      return (
        <MenuItem key={img.id} value={idx}>
          {idx}. {img.name}{count > 0 ? ` (${count})` : ''}
        </MenuItem>
      );
    });
  }, [useIndexInput, images, getAnnotationCount]);

  if (!currentImage && images.length === 0) return null;

  return (
    <>
      {images.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 56,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 'var(--z-sidebar)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'rgba(0,0,0,0.6)',
            borderRadius: 1,
            p: 1,
          }}
        >
          <IconButton
            size="small"
            sx={{ color: 'white' }}
            onClick={() => setCurrentIndex((i) => (i - 1 + images.length) % images.length)}
            disabled={images.length <= 1}
            title="Previous image"
            aria-label="Previous image"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'white', minWidth: 60, textAlign: 'center' }}>
            {currentIndex + 1} / {images.length}
          </Typography>
          <Typography variant="caption" sx={{ color: 'white', maxWidth: 160, textAlign: 'center', fontSize: '0.7rem' }}>
            {currentImage?.name ?? ''}
          </Typography>
          <IconButton
            size="small"
            sx={{ color: 'white' }}
            onClick={() => setCurrentIndex((i) => (i + 1) % images.length)}
            disabled={images.length <= 1}
            title="Next image"
            aria-label="Next image"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          top: 62,
          right: 12,
          zIndex: 'var(--z-viewer-tools)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {photoLayers.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <Select
              value={selectedLayerId ?? ''}
              onChange={(e) => onLayerChange?.(e.target.value === '' ? null : e.target.value)}
              displayEmpty
              renderValue={(val) => {
                if (val == null || val === '') return 'All layers';
                const layer = photoLayers.find((l) => l.id === val);
                return layer ? layer.name : val;
              }}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.75)',
                color: 'white',
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '& .MuiSvgIcon-root': { color: 'white' },
              }}
            >
              <MenuItem value="">All layers</MenuItem>
              {photoLayers.map((layer) => (
                <MenuItem key={layer.id} value={layer.id}>
                  {layer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {useIndexInput ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <form onSubmit={handleIndexSubmit}>
              <TextField
                inputRef={indexInputRef}
                size="small"
                type="number"
                inputProps={{ min: 1, max: images.length, step: 1 }}
                value={indexInput}
                onChange={(e) => setIndexInput(e.target.value)}
                onBlur={handleIndexSubmit}
                onKeyDown={(e) => e.key === 'Enter' && handleIndexSubmit(e)}
                sx={{
                  width: 260,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(0,0,0,0.75)',
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                  },
                }}
              />
            </form>
            <Typography variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>
              {displayIndex}. {currentImage?.name ?? ''}
              {getAnnotationCount(currentImage?.id) > 0 ? ` (${getAnnotationCount(currentImage?.id)})` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Index 1–{images.length}
            </Typography>
          </Box>
        ) : (
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <Select
              value={currentIndex}
              onChange={(e) => setCurrentIndex(Number(e.target.value))}
              displayEmpty
              renderValue={(val) => {
                const img = images[val];
                if (!img) return '';
                const count = getAnnotationCount(img.id);
                const base = `${val}. ${img.name}`;
                return count > 0 ? `${base} (${count})` : base;
              }}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.75)',
                color: 'white',
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '& .MuiSvgIcon-root': { color: 'white' },
              }}
            >
              {photoSelectOptions}
            </Select>
          </FormControl>
        )}
      </Box>
    </>
  );
}
